import { MRT_TableOptions } from 'material-react-table';

/**
 * Creates a common table configuration with consistent styling and behavior
 */
export function createTableConfig<T extends Record<string, any>>(
  options: {
    enableEditing?: boolean;
    enableRowSelection?: boolean;
    hiddenColumns?: string[];
    onEditingCancel?: () => void;
    onCreatingCancel?: () => void;
  } = {}
): Partial<MRT_TableOptions<T>> {
  const { enableEditing = true, enableRowSelection = false, hiddenColumns = [], onEditingCancel = () => {}, onCreatingCancel = () => {} } = options;

  // Convert hidden columns array to visibility object
  const columnVisibility: Record<string, boolean> = {};
  hiddenColumns.forEach((column) => {
    columnVisibility[column] = false;
  });

  return {
    // Disable features we don't need
    enableSorting: true,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableMultiSort: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableRowActions: false,
    enableBottomToolbar: false,
    enableStickyHeader: true,
    enableColumnPinning: true,

    initialState: {
      columnVisibility,
      columnPinning: { left: ['mrt-row-actions'] },
    },
    positionActionsColumn: 'first',

    // Row selection settings
    enableRowSelection,
    enableMultiRowSelection: enableRowSelection,

    // Table styling
    muiTableContainerProps: { sx: { height: '100%' } },
    muiTablePaperProps: { sx: { height: '100%' } },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#F9FAFB',
        '& .Mui-active': { color: '#00A651' },
      },
    },

    // Editing configuration
    enableEditing,
    createDisplayMode: 'row' as const,
    editDisplayMode: 'row' as const,
    onCreatingRowCancel: onCreatingCancel,
    onEditingRowCancel: onEditingCancel,
  };
}

/**
 * Utility to handle entity operations with proper error handling
 */
export function createEntityHandler<T extends Record<string, any>>(options: {
  upsertFunction: (entity: T) => Promise<any>;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}) {
  const { upsertFunction, onSuccess = () => {}, onError = () => {} } = options;

  return async (
    values: Record<string, any>,
    exitMode: () => void,
    options: {
      id?: string | number;
      isUpdate?: boolean;
      prepareEntity: (values: Record<string, any>, id?: string | number) => T;
    }
  ) => {
    const { id, isUpdate = false, prepareEntity } = options;

    try {
      // Prepare entity data
      const entity = prepareEntity(values, id);

      // Execute upsert operation
      await upsertFunction(entity);

      // Success message
      onSuccess(`Entity ${isUpdate ? 'updated' : 'created'} successfully`);
    } catch (error) {
      // Error handling
      onError(`Entity ${isUpdate ? 'update' : 'creation'} failed`);
    } finally {
      exitMode();
    }
  };
}
